import React, { useState, useCallback } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { GripVertical, Plus, X, Trophy, DollarSign, Infinity } from 'lucide-react';
import { cn } from '@/lib/utils';

const PREPOPULATED_GOALS = [
  { id: 'wealth_building', label: 'General Wealth Building', placeholder: 'Leave blank for ongoing' },
  { id: 'retirement', label: 'Retirement', placeholder: 'Target retirement savings' },
  { id: 'home', label: 'Buying a Home', placeholder: 'Down payment amount' },
  { id: 'education', label: "Children's Education", placeholder: 'Education fund target' },
  { id: 'debt_repayment', label: 'Debt Repayment', placeholder: 'Total debt to pay off' },
  { id: 'specific_purchase', label: 'Specific Purchase (Car/Wedding)', placeholder: 'Purchase amount' },
  { id: 'emergency_fund', label: 'Emergency Fund', placeholder: 'Target emergency fund' },
];

const RANK_LABELS = ['Most Important', 'Second Priority', 'Third Priority'];
const RANK_COLORS = ['bg-yellow-500', 'bg-gray-400', 'bg-amber-600'];

export default function GoalSelector({ value = [], onChange }) {
  const [customGoalInput, setCustomGoalInput] = useState('');
  const [customGoals, setCustomGoals] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);

  // Parse existing value to extract custom goals
  React.useEffect(() => {
    if (value && value.length > 0) {
      const existingCustomGoals = value
        .filter(g => g.is_custom)
        .map(g => ({ id: g.goal, label: g.goal, is_custom: true }));
      if (existingCustomGoals.length > 0) {
        setCustomGoals(prev => {
          const newCustoms = existingCustomGoals.filter(
            cg => !prev.some(p => p.id === cg.id)
          );
          return [...prev, ...newCustoms];
        });
      }
    }
  }, []);

  const allGoals = [...PREPOPULATED_GOALS, ...customGoals];
  
  const selectedGoals = value || [];
  const selectedIds = selectedGoals.map(g => g.goal);

  const handleToggleGoal = (goalId, isCustom = false) => {
    const isSelected = selectedIds.includes(goalId);
    
    if (isSelected) {
      // Remove goal
      const newSelected = selectedGoals.filter(g => g.goal !== goalId);
      // Re-rank remaining goals
      const reranked = newSelected.map((g, idx) => ({ ...g, rank: idx + 1 }));
      onChange(reranked);
    } else {
      // Add goal (if under limit)
      if (selectedGoals.length >= 3) {
        return; // Max 3 goals
      }
      const newGoal = {
        rank: selectedGoals.length + 1,
        goal: goalId,
        target_amount: null, // Will be set by user
        is_custom: isCustom || customGoals.some(cg => cg.id === goalId)
      };
      onChange([...selectedGoals, newGoal]);
    }
  };

  const handleTargetAmountChange = (goalId, value) => {
    // Parse the value - remove non-numeric except decimal
    const cleanValue = value.replace(/[^0-9.]/g, '');
    const numericValue = cleanValue ? parseFloat(cleanValue) : null;
    
    const updated = selectedGoals.map(g => 
      g.goal === goalId ? { ...g, target_amount: numericValue } : g
    );
    onChange(updated);
  };

  const handleAddCustomGoal = () => {
    const trimmed = customGoalInput.trim();
    if (!trimmed || trimmed.length > 25) return;
    if (customGoals.some(cg => cg.id === trimmed)) return;
    
    const newCustomGoal = { id: trimmed, label: trimmed, is_custom: true, placeholder: 'Target amount' };
    setCustomGoals(prev => [...prev, newCustomGoal]);
    setCustomGoalInput('');
  };

  const handleRemoveCustomGoal = (goalId) => {
    setCustomGoals(prev => prev.filter(g => g.id !== goalId));
    // Also remove from selection if selected
    if (selectedIds.includes(goalId)) {
      const newSelected = selectedGoals.filter(g => g.goal !== goalId);
      const reranked = newSelected.map((g, idx) => ({ ...g, rank: idx + 1 }));
      onChange(reranked);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newSelected = [...selectedGoals];
    const draggedGoal = newSelected[draggedItem];
    newSelected.splice(draggedItem, 1);
    newSelected.splice(index, 0, draggedGoal);
    
    // Update ranks
    const reranked = newSelected.map((g, idx) => ({ ...g, rank: idx + 1 }));
    onChange(reranked);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const getGoalLabel = (goalId) => {
    const found = allGoals.find(g => g.id === goalId);
    return found ? found.label : goalId;
  };

  return (
    <div className="space-y-6">
      {/* Available Goals */}
      <div>
        <Label className="text-base font-medium mb-3 block">Available Goals</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Select up to 3 goals. {selectedGoals.length}/3 selected
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {allGoals.map((goal) => {
            const isSelected = selectedIds.includes(goal.id);
            const isDisabled = !isSelected && selectedGoals.length >= 3;
            const isCustom = goal.is_custom || customGoals.some(cg => cg.id === goal.id);
            
            return (
              <div
                key={goal.id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-lg border transition-all',
                  isSelected ? 'border-primary bg-primary/5' : 'border-border',
                  isDisabled && !isSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary/50'
                )}
              >
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id={`goal-${goal.id}`}
                    checked={isSelected}
                    disabled={isDisabled}
                    onCheckedChange={() => handleToggleGoal(goal.id, isCustom)}
                  />
                  <Label
                    htmlFor={`goal-${goal.id}`}
                    className={cn(
                      'font-normal cursor-pointer',
                      isDisabled && !isSelected ? 'cursor-not-allowed' : ''
                    )}
                  >
                    {goal.label}
                  </Label>
                </div>
                {isCustom && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCustomGoal(goal.id);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Custom Goal */}
      <div>
        <Label className="text-base font-medium mb-3 block">Add Your Own Goal</Label>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Input
              placeholder="Enter custom goal..."
              value={customGoalInput}
              onChange={(e) => setCustomGoalInput(e.target.value.slice(0, 25))}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCustomGoal()}
              maxLength={25}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {customGoalInput.length}/25
            </span>
          </div>
          <Button
            onClick={handleAddCustomGoal}
            disabled={!customGoalInput.trim() || customGoalInput.length > 25}
            size="icon"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Selected Goals - Ranking Area */}
      {selectedGoals.length > 0 && (
        <div>
          <Label className="text-base font-medium mb-3 block">Your Ranked Goals</Label>
          <p className="text-sm text-muted-foreground mb-4">
            Drag to reorder by importance. Enter a target amount, or leave blank if ongoing.
          </p>
          <div className="space-y-3">
            {selectedGoals
              .sort((a, b) => a.rank - b.rank)
              .map((goal, index) => {
                const goalInfo = allGoals.find(g => g.id === goal.goal);
                const placeholder = goalInfo?.placeholder || 'Target amount';
                
                return (
                  <div
                    key={goal.goal}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={cn(
                      'p-4 rounded-lg border bg-card cursor-move transition-all',
                      draggedItem === index ? 'opacity-50 border-primary' : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div className={cn(
                        'flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0',
                        RANK_COLORS[index] || 'bg-gray-500'
                      )}>
                        {index === 0 ? <Trophy className="w-4 h-4" /> : index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{getGoalLabel(goal.goal)}</p>
                        <p className="text-xs text-muted-foreground">{RANK_LABELS[index]}</p>
                      </div>
                    </div>
                    
                    {/* Target Amount Input */}
                    <div className="mt-3 ml-16 flex items-center gap-2">
                      <div className="relative flex-1 max-w-[200px]">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder={placeholder}
                          value={goal.target_amount ? goal.target_amount.toLocaleString() : ''}
                          onChange={(e) => handleTargetAmountChange(goal.goal, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="pl-8 h-9"
                          draggable={false}
                        />
                      </div>
                      {!goal.target_amount && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Infinity className="w-3 h-3" />
                          Ongoing
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
